/**
 * SubscriptionTypeSelector - Dual dropdown selector for subscription view kind and item type
 *
 * Layout (per screenshot requirement):
 * ┌────────────────────────────────────────┐
 * │ Subscription view kind               ▼ │  ← First dropdown (Vertical/Horizontal)
 * └────────────────────────────────────────┘
 *
 * ┌────────────────────────────────┐  ┌──┐
 * │ Item type ▼  [Checkbox left]  │  │⚙ │  ← Second dropdown + settings button
 * └────────────────────────────────┘  └──┘
 *
 * Sprint: screen-editor-paywall
 * Task: Phase 2 - Add Item Type Selector
 */

import React, { useCallback } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, IconButton, SelectChangeEvent } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import {
  SubscriptionCellType,
  SubscriptionItemType,
  V2_SUBSCRIPTION_CELL_TYPE_OPTIONS,
  V2_SUBSCRIPTION_ITEM_TYPE_OPTIONS,
} from '../../../../config/v2PaywallSubscriptionsConfig';
import { FIELD_MARGIN_BOTTOM } from '../../../../config/ui';
import {
  expandedToggleButtonSx,
  expandedToggleButtonActiveSx,
} from '../../../../config/editorFieldStyles';

type SelectorOption<T extends string> = { value: T; label: string };

export interface SubscriptionTypeSelectorProps<
  TViewKind extends string = SubscriptionCellType,
  TItemType extends string = SubscriptionItemType,
> {
  /** Current view kind (vertical/horizontal) */
  viewKind: TViewKind;

  /** Callback when view kind changes */
  onViewKindChange: (newViewKind: TViewKind) => void;

  /** Current item type (checkbox position) */
  itemType: TItemType;

  /** Callback when item type changes */
  onItemTypeChange: (newItemType: TItemType) => void;

  /** Override view kind options for V2 schemas. Defaults to V1 values. */
  viewKindOptions?: readonly SelectorOption<TViewKind>[];

  /** Override item type options for V2 schemas. Defaults to V1 values. */
  itemTypeOptions?: readonly SelectorOption<TItemType>[];

  /** Callback when gear icon clicked to expand/collapse styles */
  onStylesClick?: () => void;

  /** Whether control is disabled */
  disabled?: boolean;

  /** Whether styles panel is expanded */
  stylesExpanded?: boolean;
}

/**
 * SubscriptionTypeSelector - Dual dropdown for view kind and item type
 */
export function SubscriptionTypeSelector<
  TViewKind extends string = SubscriptionCellType,
  TItemType extends string = SubscriptionItemType,
>({
  viewKind,
  onViewKindChange,
  itemType,
  onItemTypeChange,
  viewKindOptions,
  itemTypeOptions,
  onStylesClick,
  disabled = false,
  stylesExpanded = false,
}: SubscriptionTypeSelectorProps<TViewKind, TItemType>): React.ReactElement {
  const resolvedViewKindOptions = (viewKindOptions ?? V2_SUBSCRIPTION_CELL_TYPE_OPTIONS) as readonly SelectorOption<TViewKind>[];
  const resolvedItemTypeOptions = (itemTypeOptions ?? V2_SUBSCRIPTION_ITEM_TYPE_OPTIONS) as readonly SelectorOption<TItemType>[];

  // Handle view kind change
  const handleViewKindChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newValue = event.target.value as TViewKind;
      if (newValue !== viewKind) {
        onViewKindChange(newValue);
      }
    },
    [viewKind, onViewKindChange]
  );

  // Handle item type change
  const handleItemTypeChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newValue = event.target.value as TItemType;
      if (newValue !== itemType) {
        onItemTypeChange(newValue);
      }
    },
    [itemType, onItemTypeChange]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* First dropdown: Subscription view kind (no gear button) */}
      <FormControl size="small" fullWidth>
        <InputLabel id="subscription-view-kind-select-label">Subscription view kind</InputLabel>
        <Select
          labelId="subscription-view-kind-select-label"
          id="subscription-view-kind-select"
          value={viewKind}
          label="Subscription view kind"
          onChange={handleViewKindChange}
          disabled={disabled}
          data-testid="subscription-view-kind-selector"
        >
          {resolvedViewKindOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Second dropdown: Item type (with gear button) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: FIELD_MARGIN_BOTTOM,
        }}
      >
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel id="subscription-item-type-select-label">Item type</InputLabel>
          <Select
            labelId="subscription-item-type-select-label"
            id="subscription-item-type-select"
            value={itemType}
            label="Item type"
            onChange={handleItemTypeChange}
            disabled={disabled}
            data-testid="subscription-item-type-selector"
          >
            {resolvedItemTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {onStylesClick && (
          <IconButton
            onClick={onStylesClick}
            disabled={disabled}
            size="small"
            data-testid="subscription-style-settings-button"
            sx={stylesExpanded ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
          >
            <TuneIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

export default SubscriptionTypeSelector;
